// src/utils/fieldUtils.js

export const getSignalStatus = (rssi) => {
    const rssiValue = parseInt(rssi, 10);

    if (rssiValue > -85) {
        return 'well_signal';
    } else if (rssiValue <= -85 && rssiValue >= -95) {
        return 'half_signal';
    } else if (rssiValue <= -96 && rssiValue >= -119) {
        return 'low_signal';
    } else if (rssiValue <= -120) {
        return 'no_signal';
    }
};

export const getBatteryStatus = (battery) => {
    const batteryValue = Math.max(0, Math.min(parseInt(battery, 10), 100));

    if (batteryValue > 70) {
        return { status: 'well_battery', value: batteryValue };
    } else if (batteryValue <= 70 && batteryValue >= 30) {
        return { status: 'half_battery', value: batteryValue };
    } else if (batteryValue <= 30 && batteryValue > 0) {
        return { status: 'low_battery', value: batteryValue };
    } else if (batteryValue === 0) {
        return { status: 'no_battery', value: batteryValue };
    }
};

export const getModel = (field) => {
    if (field['active_prediction_model']){
        let value = 0;
        let color = 'green';
        let generation = 0;
        let value_j2 = 100;
        let color_j2 = 'red';
        let generation_j2 = 0;
        if (field['active_prediction_model']['last_log_entry']){
            value = parseInt(field['active_prediction_model']['last_log_entry']['ratio_eggs'] * 100);
            generation = field['active_prediction_model']['last_log_entry']['generation']
            if (value >= 80 && value < 100) {
                color = 'orange';
            } else if (value >= 100 && value < 120) {
                value = 100;
                color = 'red';
            } else if (value >= 120) {
                value = 100;
                color = 'blue';
            }
        }
        if (field['active_prediction_model']['last_log_entry_j2']){
            generation_j2 = field['active_prediction_model']['last_log_entry_j2']['generation'] 
            value_j2 = field['active_prediction_model']['last_log_entry_j2']['ratio_larvicide']
            color_j2 = field['active_prediction_model']['last_log_entry_j2']['color']
        }
        return ['Nematode active',generation,value,color,generation_j2,value_j2,color_j2];
    }
    else if (field['active_solarization']){
        let degrees = 0;
        if (field['active_solarization']['degrees']){
            degrees = parseInt(field['active_solarization']['degrees']);
        }
        let widthBar = (degrees/3000) * 100;
        const pathogens = Array.isArray(field['active_solarization']['pathogens_latest'])
            ? field['active_solarization']['pathogens_latest']
            : [];
        return ['Solarization active', degrees, widthBar, pathogens];
    }
    return ['No model'];
};

export const getModelName = (field) => {
    if (field['active_prediction_model']){
        return 'nematode';
    }
    else if (field['active_solarization']){
        return 'solarization';
    }
    return 'no_model';
};

export const getModelId = (field) => {
    if (field['active_prediction_model']){
        return ["N",field['active_prediction_model']['id']];
    }
    else if (field['active_solarization']){
        return ["S",field['active_solarization']['id']];
    }
    return null;
};
export const getOwner = (field) => {
    return field['owner']['email'];
};

/** Tope de grados acumulados para barra / eje Nematoda (coherente con el gráfico de solarización). */
export const SOLARIZATION_NEMATODE_DEGREES_CAP = 7000;

/** Porcentaje de barra: progreso lineal grados / tope (0–100 %). */
export const getSolarizationBarPercent = (rawDegrees) => {
    const d = Math.max(0, Number(rawDegrees) || 0);
    if (SOLARIZATION_NEMATODE_DEGREES_CAP <= 0) return 0;
    return Math.min(100, (d / SOLARIZATION_NEMATODE_DEGREES_CAP) * 100);
};

/** Clave i18n bajo `field_card.*` para el nivel según grados acumulados. */
export const getSolarizationTierKey = (rawDegrees) => {
    const d = Number(rawDegrees) || 0;
    if (d < 50) return 'null';
    if (d < 500) return 'low';
    if (d < 1500) return 'moderate';
    if (d < 3000) return 'good';
    if (d < 6000) return 'excellent';
    return 'super';
};

/** Integral térmica letal (ºC acum.) por código de patógeno — alineado con backend OTHER_SOLARIZATION_PARAMS. */
export const PATHOGEN_LETHAL_THERMAL_INTEGRAL = {
    FOM: 954.0,
    RS: 480.0,
    SS: 1096.0,
    PC: 1096.0,
};

export const PATHOGEN_SOLARIZATION_ORDER = ['FOM', 'PC', 'RS', 'SS'];

/** Barra 0–100 %: grados acumulados del patógeno / integral letal. */
export const getPathogenSolarizationBarPercent = (rawDegrees, pathogenCode) => {
    const lethal = PATHOGEN_LETHAL_THERMAL_INTEGRAL[pathogenCode];
    const d = Math.max(0, Number(rawDegrees) || 0);
    if (lethal == null || lethal <= 0) return 0;
    return Math.min(100, (d / lethal) * 100);
};

/**
 * Mismo conjunto de niveles que la solarización global (field_card.null … super),
 * por quintiles del avance (grados / integral letal): 0–20% null, 20–40% low,
 * 40–60% moderate, 60–80% good, 80–100% excellent, alcanzada o superada super.
 */
export const getPathogenSolarizationTierKey = (rawDegrees, pathogenCode) => {
    const lethal = PATHOGEN_LETHAL_THERMAL_INTEGRAL[pathogenCode];
    const d = Math.max(0, Number(rawDegrees) || 0);
    if (lethal == null || lethal <= 0) return 'null';
    const ratio = d / lethal;
    if (ratio < 1 / 5) return 'null';
    if (ratio < 2 / 5) return 'low';
    if (ratio < 3 / 5) return 'moderate';
    if (ratio < 4 / 5) return 'good';
    if (ratio < 1) return 'excellent';
    return 'super';
};

export const sortPathogensLatest = (pathogens) => {
    if (!Array.isArray(pathogens)) return [];
    return [...pathogens].sort((a, b) => {
        const ia = PATHOGEN_SOLARIZATION_ORDER.indexOf(a.pathogen_code);
        const ib = PATHOGEN_SOLARIZATION_ORDER.indexOf(b.pathogen_code);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
};

/** Claves en `license_info` que autorizan la fila de solarización por patógeno (API). */
export const PATHOGEN_SOLARIZATION_LICENSE_KEY = {
    FOM: 'solarization_fom',
    RS: 'solarization_rs',
    SS: 'solarization_ss',
    PC: 'solarization_pc',
};

/** True si la licencia incluye ese patógeno en solarización (requiere flag explícito `true`). */
export const isPathogenSolarizationLicensed = (licenseInfo, pathogenCode) => {
    if (!licenseInfo) return false;
    const licKey = PATHOGEN_SOLARIZATION_LICENSE_KEY[pathogenCode];
    if (!licKey) return false;
    return licenseInfo[licKey] === true;
};

/**
 * Filas de patógeno para la tarjeta/campo: `pathogens_latest` puede ir vacío hasta que existan logs v2.
 * Rellena con una fila por código licenciado faltante (grados 0) para que la UI coincida con la licencia.
 */
export const mergeLicensedSolarPathogenRows = (pathogensLatest, licenseInfo) => {
    const raw = Array.isArray(pathogensLatest) ? pathogensLatest : [];
    if (!licenseInfo) {
        return sortPathogensLatest(raw);
    }
    const seen = new Set();
    const rows = [];
    for (const p of raw) {
        if (!p || p.pathogen_code == null || p.pathogen_code === '') continue;
        const pathogen_code = String(p.pathogen_code).toUpperCase();
        if (!isPathogenSolarizationLicensed(licenseInfo, pathogen_code)) continue;
        if (seen.has(pathogen_code)) continue;
        seen.add(pathogen_code);
        rows.push({ ...p, pathogen_code });
    }
    for (const pathogen_code of PATHOGEN_SOLARIZATION_ORDER) {
        if (!isPathogenSolarizationLicensed(licenseInfo, pathogen_code)) continue;
        if (seen.has(pathogen_code)) continue;
        seen.add(pathogen_code);
        rows.push({ pathogen_code, degrees: 0 });
    }
    return sortPathogensLatest(rows);
};

/** Nombres científicos (latín) por código de patógeno — sin i18n. */
export const SOLARIZATION_PATHOGEN_LATIN_LABEL = {
    FOM: 'Fusarium',
    PC: 'Phytophthora',
    RS: 'Rhizoctonia',
    SS: 'Sclerotinia',
};

export const getPathogenLatinLabel = (pathogenCode) =>
    SOLARIZATION_PATHOGEN_LATIN_LABEL[pathogenCode] || pathogenCode;

export const getColor = (color) => {
    switch (color) {
        case 'red':
            return '#ED5F55';
        case 'orange':
            return '#F6894E';
        case 'green':
            return '#7ECA22';
        case 'blue':
            return '#67AAFB';
    }
}

export const getColorFooter = (color) => {
    switch (color) {
        case 'red':
            return '#D5554C';
        case 'orange':
            return '#DC7B46';
        case 'green':
            return '#71B61F';
        case 'blue':
            return '#5D99E2';
    }
}

export const getColorCard = (model) => {
    if (model[0] === 'Solarization active') {
        return '#F5A544';
    } else if (model[0] === 'No model') {
        return '#C7C7C7';
    } else {
        if (model[3] === 'red' || model[6] === 'red') {
            return getColor('red');
        } 
        if (model[3] === 'orange' || model[6] === 'orange') {
            return getColor('orange');
        }
        if (model[3] === 'blue' || model[6] === 'blue') {
            return getColor('blue');
        }
        if (model[3] === 'green' || model[6] === 'green') {
            return getColor('green');
        }
    }
}

export const getColorCardFooter = (model) => {
    if (model[0] === 'Solarization active') {
        return '#DB943C';
    } else if (model[0] === 'No model') {
        return '#B3B3B3';
    } else {
        if (model[3] === 'red' || model[6] === 'red') {
            return getColorFooter('red');
        } 
        if (model[3] === 'orange' || model[6] === 'orange') {
            return getColorFooter('orange');
        }
        if (model[3] === 'blue' || model[6] === 'blue') {
            return getColorFooter('blue');
        }
        if (model[3] === 'green' || model[6] === 'green') {
            return getColorFooter('green');
        }
    }
}